import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { PixieTask } from './types';
import { PixieTaskItem } from './PixieTaskItem';

// --- TYPE DEFINITIONS ---

interface PixieSectionProps {
  title: string;
  tasks: PixieTask[];
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  onTaskCancel?: (taskId: string) => void;
  onTaskAction?: (taskId: string) => void;
  className?: string;
}

// --- CONSTANTS ---

const DEFAULT_COLLAPSE_THRESHOLD = 3;

// --- MAIN COMPONENT ---

const PixieSection: React.FC<PixieSectionProps> = ({
  title,
  tasks = [],
  collapsible = true,
  defaultCollapsed = false,
  onTaskCancel,
  onTaskAction,
  className = '',
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed || (collapsible && tasks.length > DEFAULT_COLLAPSE_THRESHOLD));

  const handleToggle = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  if (tasks.length === 0) {
    return null;
  }

  return (
    <div className={cn('border-t border-gray-100', className)}>
      <div
        className={cn('flex items-center justify-between p-4', collapsible && 'cursor-pointer')}
        onClick={handleToggle}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleToggle()}
        tabIndex={collapsible ? 0 : -1}
        role={collapsible ? 'button' : 'region'}
        aria-expanded={!isCollapsed}
      >
        <h3 className="text-sm font-semibold text-gray-700">{title} ({tasks.length})</h3>
        {collapsible && (
          isCollapsed ? <ChevronDown className="h-5 w-5 text-gray-500" /> : <ChevronUp className="h-5 w-5 text-gray-500" />
        )}
      </div>
      {!isCollapsed && (
        <div>
          {tasks.map((task) => (
            <PixieTaskItem
              key={task.id}
              task={task}
              onCancel={onTaskCancel}
              onAction={onTaskAction}
            />
          ))}
        </div>
      )}
    </div>
  );
};

PixieSection.displayName = 'PixieSection';

export { PixieSection };
